<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, DatePicker } from '@ui'

/**
 * A calendar day, `YYYY-MM-DD`, picked with the kit's date picker. Due dates
 * and target dates are days, not instants: the picker works in instants, so
 * the day is read off the reader's own calendar, as the Overview's period
 * picker does. `clearable` offers going back to no day at all.
 */
const day = defineModel<string | null>({ required: true })
const props = withDefaults(defineProps<{ min?: string; clearable?: boolean }>(), { min: undefined, clearable: false })
const { t, locale } = useI18n()

const pad = (value: number): string => String(value).padStart(2, '0')
const dayOf = (value: Date | string | number): string => {
  const date = new Date(value)
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}
const dateOf = (value: string): Date => {
  const [year, month, date] = value.split('-').map(Number)
  return new Date(year ?? 1970, (month ?? 1) - 1, date ?? 1)
}

const picked = computed({
  get: () => (day.value ? dateOf(day.value) : null),
  set: (value: Date | string | null) => {
    day.value = value ? dayOf(value) : null
  },
})
const minDate = computed(() => (props.min ? dateOf(props.min) : undefined))
</script>

<template>
  <div class="day-picker">
    <DatePicker v-model="picked" :min="minDate" :locale="locale" :show-time="false" />
    <Button v-if="clearable && day" size="sm" type="tertiary" data-test="day-clear" @click="day = null">
      {{ t('actions.form.clearDate') }}
    </Button>
  </div>
</template>

<style scoped>
.day-picker {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-sm);
}
</style>
